import { useEffect, useState } from "react";
import api, { money, num } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useMeta } from "@/context/MetaContext";
import { PageHeader, Loading } from "@/components/common/PageHeader";
import { KpiCard } from "@/components/common/KpiCard";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell,
  BarChart, Bar, CartesianGrid, Legend,
} from "recharts";
import {
  TrendingUp, Receipt, Wallet, PiggyBank, Boxes, Percent, AlertTriangle,
  CheckSquare, Truck, Utensils, ShoppingCart, ArrowRight,
} from "lucide-react";
import { Link } from "react-router-dom";
import { CHART_GRID, CHART_TICK, CHART_TOOLTIP, CHART_CURSOR } from "@/lib/chart";

const CHART = ["#D9381E", "#64748b", "#f0a020", "#3a9d5d", "#7351b7", "#0ea5e9", "#a3a3a3"];

function ChartCard({ title, children, action }) {
  return (
    <div className="rounded-sm border border-border bg-card p-5">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="font-display text-base font-bold tracking-tight">{title}</h3>
        {action}
      </div>
      {children}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const { outletOptions, outletName } = useMeta();
  const isSuper = user?.role === "super_admin" || user?.role === "finance";
  const [outlet, setOutlet] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [data, setData] = useState(null);
  const [trends, setTrends] = useState([]);
  const [cats, setCats] = useState([]);
  const [channels, setChannels] = useState([]);
  const [comparison, setComparison] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const params = { outlet_id: outlet || undefined, from_date: from || undefined, to_date: to || undefined };
      try {
        const [s, t, c, ch, cmp] = await Promise.all([
          api.get("/dashboard/summary", { params }),
          api.get("/dashboard/trends", { params: { outlet_id: outlet || undefined } }),
          api.get("/dashboard/breakdown", { params: { outlet_id: outlet || undefined, dim: "category" } }),
          api.get("/dashboard/breakdown", { params: { outlet_id: outlet || undefined, dim: "channel" } }),
          isSuper ? api.get("/dashboard/outlet-comparison") : Promise.resolve({ data: [] }),
        ]);
        setData(s.data); setTrends(t.data); setCats(c.data); setChannels(ch.data); setComparison(cmp.data);
      } catch (e) { console.error("Dashboard load failed", e); }
      finally { setLoading(false); }
    };
    load();
  }, [outlet, from, to, isSuper]);

  if (loading && !data) return <Loading label="Loading dashboard" />;
  const d = data || {};

  const kpis = [
    { label: "Net Revenue", value: money(d.revenue), icon: TrendingUp, accent: "bg-primary/10 text-primary", sub: `${money(d.gross_sales)} gross` },
    { label: "Expenses", value: money(d.expenses), icon: Receipt, accent: "bg-amber-100 text-amber-700", sub: `${money(d.discounts)} discounts` },
    { label: "COGS / Food Cost", value: money(d.cogs), icon: Utensils, accent: "bg-neutral-200 text-neutral-700", sub: `${d.food_cost_pct}% of revenue` },
    { label: "Gross Profit", value: money(d.gross_profit), icon: PiggyBank, accent: "bg-emerald-100 text-emerald-700", sub: `${d.gross_margin}% margin` },
    { label: "Net Profit", value: money(d.net_profit), icon: TrendingUp, accent: "bg-emerald-100 text-emerald-700", sub: `${d.net_margin}% margin` },
    { label: "Inventory Value", value: money(d.inventory_value), icon: Boxes, accent: "bg-sky-100 text-sky-700", sub: "current stock" },
    { label: "Purchases", value: money(d.purchases), icon: ShoppingCart, accent: "bg-neutral-200 text-neutral-700", sub: `${money(d.supplier_dues)} dues` },
    { label: "Wastage", value: money(d.wastage), icon: Percent, accent: "bg-red-100 text-red-700", sub: "period total" },
  ];

  const alerts = [
    { icon: CheckSquare, label: "Pending approvals", value: d.pending_approvals, to: "/approvals", color: "text-amber-600" },
    { icon: AlertTriangle, label: "Low stock items", value: d.low_stock, to: "/inventory", color: "text-red-600" },
    { icon: Truck, label: "Supplier dues", value: money(d.supplier_dues), to: "/procurement", color: "text-sky-600" },
    { icon: Wallet, label: "Budget variance", value: money(d.budget_variance), to: "/budget", color: d.budget_variance >= 0 ? "text-emerald-600" : "text-red-600" },
  ];

  return (
    <div className="space-y-6">
      <PageHeader title="Dashboard" subtitle={`Chain overview · ${outletName(outlet)}`}>
        <div className="flex flex-wrap items-center gap-2">
          {isSuper && (
            <Select value={outlet || "all"} onValueChange={(v) => setOutlet(v === "all" ? "" : v)}>
              <SelectTrigger className="w-44" data-testid="dash-outlet-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Outlets</SelectItem>
                {outletOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-40" data-testid="dash-from" />
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-40" data-testid="dash-to" />
        </div>
      </PageHeader>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {kpis.map((k, i) => <KpiCard key={k.label} testId={`kpi-${i}`} {...k} />)}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <ChartCard title="Revenue vs Expense Trend">
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={trends} margin={{ left: -10, right: 10, top: 10 }}>
                <defs>
                  <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#D9381E" stopOpacity={0.3} /><stop offset="100%" stopColor="#D9381E" stopOpacity={0} /></linearGradient>
                  <linearGradient id="exp" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#64748b" stopOpacity={0.25} /><stop offset="100%" stopColor="#64748b" stopOpacity={0} /></linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID} vertical={false} />
                <XAxis dataKey="date" tick={CHART_TICK} tickFormatter={(v) => v?.slice(5)} minTickGap={24} />
                <YAxis tick={CHART_TICK} tickFormatter={(v) => `${v / 1000}k`} />
                <Tooltip formatter={(v) => money(v)} contentStyle={{ borderRadius: 4, border: "1px solid #e5e5e5", fontSize: 12 }} />
                <Area type="monotone" dataKey="revenue" stroke="#D9381E" strokeWidth={2} fill="url(#rev)" name="Revenue" />
                <Area type="monotone" dataKey="expense" stroke="#64748b" strokeWidth={2} fill="url(#exp)" name="Expense" />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>

        <div className="space-y-4">
          {alerts.map((a, i) => (
            <Link key={a.label} to={a.to} data-testid={`alert-${i}`} className="flex items-center justify-between rounded-sm border border-border bg-card p-4 transition-colors hover:bg-secondary/50">
              <div className="flex items-center gap-3">
                <span className="rounded-sm bg-secondary p-2"><a.icon className={`h-4 w-4 ${a.color}`} /></span>
                <span className="text-sm font-medium">{a.label}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className={`font-display text-lg font-bold tabular ${a.color}`}>{a.value}</span>
                <ArrowRight className="h-4 w-4 text-muted-foreground" />
              </div>
            </Link>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <ChartCard title="Sales by Category">
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={cats} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={95} paddingAngle={2}>
                {cats.map((c, i) => <Cell key={c.name || i} fill={CHART[i % CHART.length]} />)}
              </Pie>
              <Tooltip formatter={(v) => money(v)} contentStyle={CHART_TOOLTIP} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Sales by Channel">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={channels} margin={{ left: -10, right: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID} vertical={false} />
              <XAxis dataKey="name" tick={CHART_TICK} />
              <YAxis tick={CHART_TICK} tickFormatter={(v) => `${v / 1000}k`} />
              <Tooltip formatter={(v) => money(v)} contentStyle={CHART_TOOLTIP} cursor={CHART_CURSOR} />
              <Bar dataKey="value" radius={[3, 3, 0, 0]} fill="#D9381E" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {isSuper && comparison.length > 0 && (
        <ChartCard title="Outlet Comparison">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={comparison} margin={{ left: -10, right: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID} vertical={false} />
              <XAxis dataKey="outlet" tick={CHART_TICK} />
              <YAxis tick={CHART_TICK} tickFormatter={(v) => `${v / 1000}k`} />
              <Tooltip formatter={(v) => money(v)} contentStyle={CHART_TOOLTIP} cursor={CHART_CURSOR} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="revenue" name="Revenue" fill="#D9381E" radius={[3, 3, 0, 0]} />
              <Bar dataKey="expense" name="Expense" fill="#64748b" radius={[3, 3, 0, 0]} />
              <Bar dataKey="profit" name="Profit" fill="#3a9d5d" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      )}
    </div>
  );
}
